import {
  licenseIsInfinite,
  renderLicenseCredits,
  renderLicenseExtrapack,
} from "./LicenceCredits";
import { LicenseStatus } from "../../redux/actions/licenses/licenceTypes";
import { unlinkLicenseUser } from "../../redux/actions/licenses/licenseListAsyncActions";
import { ThunkDispatch } from "redux-thunk";
import { GridDeleteIcon } from "@mui/x-data-grid-pro";
import { Add } from "@mui/icons-material";
import { TFunction } from "i18next";
import { Button, Skeleton, Tooltip } from "@mui/material";
import {
  focusedLicense,
  LicensesFilters,
  selectLicencesAndOwners,
  setLicenseId,
  setPreselectedFilterField,
  setShowFilterForm,
} from "../../redux/slices/licenses/licenseList";
import { useDispatch, useSelector } from "react-redux";
import { getOperatorsFromAvailableFilters } from "../helpers/helpersSearchs";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { OrgGroupTypeIds } from "../../redux/actions/users/userTypes";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";

const useSetActions = () => {
  const dispatch = useDispatch();
  return {
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField(field));
    },

    setLicenseId: (licenseId: string) => {
      dispatch(setLicenseId(licenseId));
    },
  };
};

export const useLicenseColumns = (
  t: TFunction,
  dispatch: ThunkDispatch<any, any, any>,
) => {
  const { onOpenFilter, setLicenseId } = useSetActions();
  const { filterModel } = useSelector(LicensesFilters);
  const licenses = useSelector(selectLicencesAndOwners);

  let licenseColumns: any[] = [
    {
      field: "username",
      headerName: t("Data.table.Username"),
      disableColumnMenu: true,
      width: 210,
      sortable: false,
      type: "string",
      filterOperators: getOperatorsFromAvailableFilters([
        "$ilike",
        "$eq",
        "$ne",
      ]),
      filter: "LOCAL",
      service: "text",
      renderHeader: () => (
        <CustomGeneralColumnHeader
          headerName={t("Data.table.Username")}
          field={"username"}
          filterModel={filterModel}
          availableFilters={["$ilike", "$eq", "$ne"]}
          onOpenFilter={onOpenFilter}
          backgroundColor="#fafafa"
        />
      ),
      renderCell(params) {
        const licenseId = params.id;
        const license = params.row?.license;
        const username = params.row?.username;

        if (!params.row || !license) {
          return null;
        }
        const isUnassigned =
          !license.ownerGroupId ||
          license.ownerGroup?.typeId !== OrgGroupTypeIds.user;

        if (username) {
          return <>{username}</>;
        }

        if (isUnassigned) {
          return (
            <div style={LicenseStylesColums.buttonContainer}>
              <Button
                onClick={() => setLicenseId(licenseId)}
                sx={{
                  textTransform: "none",
                }}
              >
                {t("licenses.assignUser")}
              </Button>
            </div>
          );
        }

        if (license.ownerGroupId && !username) {
          return <Skeleton variant="text" width="100%" height={40} />;
        }
        return null;
      },
    },
    {
      field: "siteName",
      headerName: t("Dashboard.activityTable.site"),
      disableColumnMenu: true,
      width: 255,
      sortable: false,
      type: "string",
      filterOperators: getOperatorsFromAvailableFilters([
        "$ilike",
        "$eq",
        "$ne",
      ]),
      filter: "LOCAL",
      service: "text",
      renderHeader: () => (
        <CustomGeneralColumnHeader
          headerName={t("Dashboard.activityTable.site")}
          field={"siteName"}
          filterModel={filterModel}
          availableFilters={["$ilike", "$eq", "$ne"]}
          onOpenFilter={onOpenFilter}
          backgroundColor="#fafafa"
        />
      ),
      renderCell(params) {
        const license = params.row?.license;
        const siteName = params.row?.siteName;
        const username = params.row?.username;

        if (!params.row || !license) {
          return null;
        }

        if (siteName) {
          return <>{siteName}</>;
        }

        if (license.ownerGroupId && !username) {
          return <Skeleton variant="text" width="100%" height={40} />;
        }

        return null;
      },
    },
    {
      field: "balance",
      headerName: t("licenses.balance"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        return renderLicenseCredits(
          [params.row.license],
          t,
          t("licenses.addLicense"),
          t("licenses.expired"),
        );
      },
    },
    {
      field: "extra",
      headerName: t("licenses.extra_pack"),
      disableColumnMenu: true,
      width: 190,
      sortable: false,
      type: "string",

      renderCell(params) {
        return (
          <Tooltip title={t("licenses.extra_pack_desc")} placement="top" arrow>
            <span>
              {renderLicenseExtrapack(
                [params.row.license],
                t("licenses.addLicense"),
                t("licenses.expired"),
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "expiryDate",
      headerName: t("licenses.expiration"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        return (
          <Tooltip
            title={t("licenses.licenseValidationDesc")}
            placement="top"
            arrow
          >
            <span>{new Date(params.value).toLocaleDateString()}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "totalCreditsSinceStart",
      headerName: t("licenses.totalCreditsSinceStart"),
      disableColumnMenu: true,
      width: 180,
      sortable: true,
    },

    {
      field: "",
      headerName: t("licenses.addPack"),
      disableColumnMenu: true,
      width: 100,
      sortable: false,
      renderCell(params) {
        return licenseIsInfinite(params.row.license) ||
          !(params.row.license as any).hasPacks ? null : (
          <Button
            color="info"
            onClick={() => dispatch(focusedLicense(params.row.id))}
          >
            <Add />
          </Button>
        );
      },
    },
    {
      field: "id",
      headerName: t("licenses.unlinkUser"),
      disableColumnMenu: true,
      width: 120,
      sortable: false,
      renderCell(params) {
        return (
          <Button
            disabled={
              params.row.license.statusConstantId === LicenseStatus.expired ||
              !params.row.username
            }
            color="error"
            onClick={() => dispatch(unlinkLicenseUser(params.value))}
          >
            <GridDeleteIcon />
          </Button>
        );
      },
    },
  ];

  let licensesRows: any[] = licenses.map((license) => {
    const rows = {
      id: license.id,
      username: license?.username,
      siteName: license?.siteName,
      balance: license.availableCredits,
      expiryDate: license.expiryDate,
      license: license,
      totalCreditsSinceStart: license?.metadata?.totalCreditsSinceStart
        ? license.metadata.totalCreditsSinceStart / 1000
        : "",
    };
    return rows;
  });
  return { licenseColumns, licensesRows };
};
